import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ApparelProduct, Student, Family } from "@/types/models";
import ApparelService from "@/services/apparelService";
import { Button } from "@/components/ui/button";
import { getCurrentOrgId } from "@/lib/auth";
import ProductList from "./order-form/ProductList";
import OrderDetailsForm from "./order-form/OrderDetailsForm";
import { useAuth } from "@/hooks/useAuth";

const formSchema = z.object({
  family_id: z.string({
    required_error: "Family is required",
  }),
  student_id: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ApparelOrderFormProps {
  selectedProducts: Array<{
    product: ApparelProduct;
    quantity: number;
    size?: string;
  }>;
  students: Student[];
  onSuccess?: () => void;
  onCancel?: () => void;
  onRemoveProduct: (index: number) => void;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onUpdateSize: (index: number, size: string) => void;
}

const ApparelOrderForm = forwardRef<unknown, ApparelOrderFormProps>(
  (
    {
      selectedProducts,
      students,
      onRemoveProduct,
      onUpdateQuantity,
      onUpdateSize,
      ...props
    },
    ref: React.ForwardedRef<any>
  ) => {
    const [familyStudents, setFamilyStudents] = useState<Student[]>([]);
    const { user } = useAuth();
    const familyId = user?.user_metadata?.family_id;

    const form = useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        notes: "",
        family_id: familyId || "",
      },
    });

    useImperativeHandle(ref, () => ({
      getValues: () => form.getValues(),
    }));

    useEffect(() => {
      if (familyId) {
        const filteredStudents = students.filter(student => student.family_id === familyId);
        setFamilyStudents(filteredStudents);
        const currentStudentId = form.watch("student_id");
        if (currentStudentId && !filteredStudents.some(s => s.id === currentStudentId)) {
          form.setValue("student_id", "");
        }
      }
    }, [familyId, students, form]);

    return (
      <div className="space-y-4">
        <ProductList
          selectedProducts={selectedProducts}
          onRemoveProduct={onRemoveProduct}
          onUpdateQuantity={onUpdateQuantity}
          onUpdateSize={onUpdateSize}
        />
        <OrderDetailsForm
          form={form}
          families={[]}
          familyStudents={familyStudents}
        />
      </div>
    );
  }
);

export default ApparelOrderForm;
